"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createIntermediateCalcEvaluator = exports.checkIntermediateCalcErrors = exports.RequiredIntermediateFieldNames = exports.isRequiredIntermediateFieldName = exports.createIntermediateFieldProposals = void 0;
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const AssignmentExclusion_1 = require("./AssignmentExclusion");
const RequiredIntermediateFields_1 = require("./RequiredIntermediateFields");
var RequiredIntermediateFields_2 = require("./RequiredIntermediateFields");
Object.defineProperty(exports, "createIntermediateFieldProposals", { enumerable: true, get: function () { return RequiredIntermediateFields_2.createIntermediateFieldProposals; } });
Object.defineProperty(exports, "isRequiredIntermediateFieldName", { enumerable: true, get: function () { return RequiredIntermediateFields_2.isRequiredIntermediateFieldName; } });
Object.defineProperty(exports, "RequiredIntermediateFieldNames", { enumerable: true, get: function () { return RequiredIntermediateFields_2.RequiredIntermediateFieldNames; } });
// intermediate field names must be LTE 76 characters
// for ingestion into datalake, due to 128 char limit (there are other chars due normalized property nesting)
const intermediateFieldNameMaximumLength = 76;
const intermediateOutputsToReceivableFields = (intermediateOutputs) => intermediateOutputs.reduce((acc, { field, output }) => (Object.assign(Object.assign({}, acc), { [field]: output })), {});
// intermediate calcs are list of formulas that are later evaluated by createIntermediateCalcEvaluator
// This function pre-validates the container against given baseFields and loanTapeMetadata variables
// to ensure that formula dependencies are met.
// returns an array of fields and any missingVariables for it.
const checkIntermediateCalcErrors = ({ intermediateCalcs, baseFields, }) => {
    // ensure there are no duplicate field definitions
    const fieldSet = new Set();
    const duplicateField = intermediateCalcs.find(({ field }) => {
        if (fieldSet.has(field)) {
            return true;
        }
        fieldSet.add(field);
        return false;
    });
    if (duplicateField) {
        return [
            {
                field: duplicateField.field,
                errorMessages: [`field:[${duplicateField.field}] is duplicated`],
                missingFunctions: [],
                missingVariables: [],
            },
        ];
    }
    const intermediateFieldsExceedingLengthLimit = [];
    intermediateCalcs.forEach(({ field }) => {
        if (field.length > intermediateFieldNameMaximumLength) {
            intermediateFieldsExceedingLengthLimit.push(field);
        }
    });
    if (intermediateFieldsExceedingLengthLimit.length) {
        return intermediateFieldsExceedingLengthLimit.map((field) => ({
            field,
            errorMessages: [
                `field:[${field}] exceeds maximum length of ${intermediateFieldNameMaximumLength}`,
            ],
            missingFunctions: [],
            missingVariables: [],
        }));
    }
    // ensure required intermediate calcs
    // are set to correct field type.
    const invalidIntermediateCalcFieldTypeErrors = [];
    intermediateCalcs.forEach(({ field, fieldType }) => {
        if ((0, RequiredIntermediateFields_1.isRequiredIntermediateFieldName)(field)) {
            const validTypes = (0, RequiredIntermediateFields_1.getIntermediateCalcValidFieldTypes)(field);
            if (!validTypes.includes(fieldType)) {
                const errorMessages = [
                    `field:[${field}] has invalid type:[${fieldType}]. expected:[${validTypes.join(',')}]`,
                ];
                invalidIntermediateCalcFieldTypeErrors.push({
                    field,
                    errorMessages,
                    missingFunctions: [],
                    missingVariables: [],
                });
            }
        }
    });
    if (invalidIntermediateCalcFieldTypeErrors.length) {
        return invalidIntermediateCalcFieldTypeErrors;
    }
    const baseFieldVars = baseFields.map((key) => `baseFields.${key}`);
    const metaDataVars = finley_types_1.Receivable.CreditFacility.LoanTapeMetadataIterable.map((key) => `loanTapeMetadata.${key}`);
    const availableVariables = new Set(baseFieldVars.concat(metaDataVars));
    // ensure that all required intermediate calcs are present.
    const missingRequiredFields = RequiredIntermediateFields_1.RequiredIntermediateFieldNames.filter((requiredField) => !intermediateCalcs.find(({ field }) => field === requiredField));
    const missingRequiredFieldErrors = missingRequiredFields.map((field) => {
        return {
            field,
            errorMessages: [`Required field [${field}] is missing`],
            missingFunctions: [],
            missingVariables: [],
        };
    });
    // generate formula dependencies for each field
    const missingVariablesPerField = intermediateCalcs.map(({ field, config }) => {
        (0, common_1.finleyAssert)(config.type === 'formula', `invalid intermediate calc type:${config.type}`);
        const formulaErrors = (0, formula_engine_1.getFormulaErrors)(config.formula, availableVariables);
        // this field is available for subsequent formulas.
        availableVariables.add(field);
        return Object.assign({ field }, formulaErrors);
    });
    // filter out things that are not really missing.
    const definitelyMissing = missingVariablesPerField.filter(({ errorMessages }) => errorMessages.length > 0);
    return definitelyMissing.concat(missingRequiredFieldErrors);
};
exports.checkIntermediateCalcErrors = checkIntermediateCalcErrors;
// all the logic of evaluating intermediate calcs is encapsulated here
const createIntermediateCalcEvaluator = (intermediateCalcs) => {
    // instantiate FormulaEngine with all formula strings
    // we need to do this upfront to ensure that formula's AST are cached.
    const formulaStrings = intermediateCalcs
        .filter((intermediateCalc) => intermediateCalc.config.type === 'formula')
        .map(({ config }) => {
        (0, common_1.finleyAssert)(config.type === 'formula', 'keep TS happy');
        return config.formula;
    });
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, formulaStrings);
    const validateFieldType = (value, fieldType, field) => {
        const isEmpty = value === null || value === undefined;
        switch (fieldType) {
            case 'optionalString':
                if (isEmpty) {
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case 'string':
                (0, common_1.finleyAssert)(typeof value === 'string', `field:[${field}] evaluated to non-string value:[${value}]`);
                break;
            case 'optionalInteger':
                if (isEmpty) {
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case 'integer':
                (0, common_1.finleyAssert)(typeof value === 'number' && Number.isInteger(value), `field:[${field}] evaluated to non-integer value:[${value}]`);
                break;
            case 'optionalFloat':
                if (isEmpty) {
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case 'float':
                (0, common_1.finleyAssert)(typeof value === 'number', `field:[${field}] evaluated to non-float value:[${value}]`);
                break;
            case 'optionalBoolean':
                if (isEmpty) {
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case 'boolean':
                (0, common_1.finleyAssert)(typeof value === 'boolean', `field:[${field}] evaluated to non-boolean value:[${value}]`);
                break;
            case 'optionalDate':
                if (isEmpty) {
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case 'date':
                (0, common_1.finleyAssert)(common_1.typeguards.isFullDateStr(value), `field:[${field}] evaluated to non-date value:[${value}]`);
                break;
            case 'optionalPercentage':
                if (isEmpty) {
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case 'percentage':
                // TODO is it okay for percentage to be above 100?
                (0, common_1.finleyAssert)(typeof value === 'number', `field:[${field}] evaluated to non-percentage value:[${value}]`);
                break;
            default:
                (0, common_1.finleyAssertUnreachable)(fieldType);
        }
    };
    const evaluate = ({ baseFields, loanTapeMetadata, externalReceivableId, }) => {
        // extract individual receivable waiver data from the loanTapeMetadata
        const configuredActiveWaiversMap = lodash_1.default.keyBy(loanTapeMetadata.configuredActiveWaivers, (waiver) => waiver.externalReceivableId);
        const configuredActiveWaiversExtractor = () => {
            var _a, _b;
            return ((_b = (_a = configuredActiveWaiversMap[externalReceivableId]) === null || _a === void 0 ? void 0 : _a.borrowingBaseTestKeys) !== null && _b !== void 0 ? _b : undefined);
        };
        const receivableConfiguredActiveWaivers = configuredActiveWaiversExtractor();
        // generate FormulaInputs from baseFields + loanTapeMetadata
        // replace configuredActiveWaivers with receivable-level waivers instead of loanTape-level
        const formulaInputs = common_1.utils.generateFlattenNestedKeyObject({
            baseFields,
            loanTapeMetadata: Object.assign(Object.assign({}, loanTapeMetadata), { configuredActiveWaivers: receivableConfiguredActiveWaivers }),
        });
        formulaEngine.setVariables(formulaInputs);
        const intermediateOutputs = intermediateCalcs.map(({ field, config, fieldType }) => {
            const output = formulaEngine.evaluate(config.formula);
            // asset that it is of the correct type.
            validateFieldType(output, fieldType, field);
            // after every intermediate calc evaluation
            // set the resulting output as variable for subsequent
            // calcs to use.
            formulaEngine.setVariables({ [field]: output });
            return {
                field,
                output,
            };
        });
        const receivableFields = intermediateOutputsToReceivableFields(intermediateOutputs);
        // split receivableFields into requiredIntermediateFields and dynamicIntermediateFields
        return Object.keys(receivableFields).reduce((acc, key) => {
            const prop = key;
            if ((0, RequiredIntermediateFields_1.isRequiredIntermediateFieldName)(prop)) {
                if (prop === 'assignmentExclusion') {
                    const assignmentExclusion = receivableFields[prop];
                    (0, common_1.finleyAssert)(typeof assignmentExclusion === 'string' &&
                        AssignmentExclusion_1.AssignmentExclusionTypes.includes(assignmentExclusion), `${prop} evaluated to invalid assignment exclusion type ${assignmentExclusion}`);
                }
                // @required-fields remove spread operator, type-ing needs some work
                // as re-creating object is allowing some typechecks to slip by
                return {
                    requiredIntermediateFields: Object.assign(Object.assign({}, acc.requiredIntermediateFields), { [prop]: receivableFields[prop] }),
                    dynamicIntermediateFields: acc.dynamicIntermediateFields,
                };
            }
            return {
                requiredIntermediateFields: acc.requiredIntermediateFields,
                dynamicIntermediateFields: Object.assign(Object.assign({}, acc.dynamicIntermediateFields), { [prop]: receivableFields[prop] }),
            };
        }, {
            requiredIntermediateFields: {},
            dynamicIntermediateFields: {},
        });
    };
    return {
        evaluate,
    };
};
exports.createIntermediateCalcEvaluator = createIntermediateCalcEvaluator;
//# sourceMappingURL=intermediateCalcEvaluator.js.map